/**
 * React Query hooks for the advisory Data Quality panel.
 *
 * The validation report is stored server-side and only changes when the
 * company's financials are re-ingested, so a long stale time is appropriate.
 * A 404 resolves to `null` in the API layer (nothing stored yet) rather than
 * throwing, so there is no error state to render for that case.
 */

import { useQuery } from '@tanstack/react-query';
import {
  getValidationSummary,
  getValidationIssue,
  getFieldCoverage,
} from '../../../lib/api/dataQualityApi';
import type {
  ValidationUiResponse,
  ValidationIssueResponse,
  FieldCoverageResponse,
} from '../../../types/dataQuality';

const STALE_TIME_30_MIN = 1000 * 60 * 30;

/**
 * Fetch the panel/console payload (badge, headline, issue rows) for a company.
 *
 * @param companyId       - e.g. "ind_DABUR"; the hook is disabled without one
 * @param includePrevious - also request the previous run + trend
 * @param maxIssues       - raise the issue cap for the console's Findings view;
 *                          omit to keep the Overview strip's smaller payload.
 *                          Callers passing the same (companyId, includePrevious)
 *                          but a DIFFERENT `maxIssues` intentionally get
 *                          separate cache entries — the panel's 5-issue fetch
 *                          must not be satisfied by the console's request, or
 *                          vice versa, even though they hit the same endpoint.
 */
export const useValidationSummary = (
  companyId: string | null | undefined,
  includePrevious: boolean = true,
  maxIssues?: number
) =>
  useQuery<ValidationUiResponse | null>({
    queryKey: ['data-quality', 'summary', companyId, includePrevious, maxIssues ?? null],
    queryFn: () => getValidationSummary(companyId!, includePrevious, maxIssues),
    enabled: !!companyId,
    staleTime: STALE_TIME_30_MIN,
    retry: 1,
  });

/**
 * Fetch the full detail for one issue. Only enabled once the user opens the
 * row, so the request never fires on tab load.
 */
export const useValidationIssue = (
  companyId: string | null | undefined,
  code: string | null | undefined,
  source: string | null | undefined,
  enabled: boolean
) =>
  useQuery<ValidationIssueResponse | null>({
    queryKey: ['data-quality', 'issue', companyId, code, source],
    queryFn: () => getValidationIssue(companyId!, code!, source!),
    enabled: enabled && !!companyId && !!code && !!source,
    staleTime: STALE_TIME_30_MIN,
    retry: 1,
  });

/**
 * Fetch the Field Coverage Matrix for one chain and one year.
 *
 * Like `useValidationIssue`, this is gated on `enabled` so nothing is
 * requested until the user expands the matrix — the Overview tab must never
 * wait on a 140-field payload.
 *
 * `year` is part of the key, so switching years refetches and caches per
 * (companyId, source, year) rather than replacing the previous year's data.
 */
export const useFieldCoverage = (
  companyId: string | null | undefined,
  source: string | null | undefined,
  year: number | null | undefined,
  enabled: boolean
) =>
  useQuery<FieldCoverageResponse | null>({
    queryKey: ['data-quality', 'fields', companyId, source, year ?? null],
    queryFn: () => getFieldCoverage(companyId!, source!, year),
    enabled: enabled && !!companyId && !!source,
    staleTime: STALE_TIME_30_MIN,
    retry: 1,
    // Year switching is a filter, not a navigation: holding the previous
    // year's table while the next one loads stops the accordion collapsing
    // and the panel jumping under the cursor.
    placeholderData: (previous) => previous,
  });
